import React, { useEffect, useMemo, useState } from "react";
import { Input, Spin } from "antd";
import { useSearchParams } from "react-router-dom";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import Pagination from "../../components/global/pagination/Pagination.jsx";
import columns from "../../columns/wallet/WalletReport.jsx";
import { GetWalletReportApi } from "../../api/request/wallet/walletReport.js";
import Segmented from "../../components/segmented/Segmented.jsx";
import ExportHandler from "../../components/global/export/ExportHandler.jsx";
import useModulePermissions from "../../hooks/useModulePermissions.jsx";

const formatAmount = (value) =>
  `$ ${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const buildParams = ({
  page,
  pageSize,
  searchText,
  userType,
  sorter,
  applyPagination,
}) => {
  const params = {
    PageNumber: page,
    PageSize: pageSize,
    SearchText: searchText,
    ApplyPagination: applyPagination,
    SortBy: sorter.sortBy,
    SortOrder: sorter.sortOrder,
  };
  if (userType && userType !== "All") {
    params.UserType = userType;
  }
  return new URLSearchParams(params).toString();
};

export default function WalletReport() {
  const reportPermissions = useModulePermissions("report");
  const canExport = !!reportPermissions.export;
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [totalWalletBalance, setTotalWalletBalance] = useState(null);
  const [totalIBWalletBalance, setTotalIBWalletBalance] = useState(null);
  const [totalIBCommission, setTotalIBCommission] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [userType, setUserType] = useState("All");
  const [sorter, setSorter] = useState({
    sortBy: "CreatedDate",
    sortOrder: "desc",
  });

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const { data: res } = await GetWalletReportApi(
        buildParams({
          page,
          pageSize,
          searchText,
          userType,
          sorter,
          applyPagination: true,
        }),
      );
      if (res?.success) {
        setData(
          (res?.data ?? []).map((item) => ({
            ...item,
            key: item?.userId,
          })),
        );
        setTotalRecord(res?.totalRecords ?? 0);
        setTotalWalletBalance(res?.totalWalletBalance ?? null);
        setTotalIBWalletBalance(res?.totalIBWalletBalance ?? null);
        setTotalIBCommission(res?.totalIBCommission ?? null);
      }
    } catch (error) {
      setData([]);
      setTotalRecord(0);
      setTotalWalletBalance(null);
      setTotalIBWalletBalance(null);
      setTotalIBCommission(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const type = searchParams.get("userType");
    if (type === "User" || type === "IB") {
      setUserType(type);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, [page, pageSize, searchText, userType, sorter]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const tableColumns = useMemo(
    () =>
      columns.map((column) => {
        if (!column.sorter) return column;
        const field =
          column.dataIndex === "userDetails" ? "email" : column.dataIndex;
        const isActive = sorter.sortBy === field;
        return {
          ...column,
          sortOrder: isActive
            ? sorter.sortOrder === "ascend"
              ? "ascend"
              : "descend"
            : null,
        };
      }),
    [sorter],
  );

  const handleTableChange = (pagination, filters, tableSorter) => {
    const sorterObj = Array.isArray(tableSorter) ? tableSorter[0] : tableSorter;
    if (sorterObj?.order) {
      const sortByField =
        sorterObj?.field === "userDetails" ? "email" : sorterObj?.field;
      setSorter({
        sortBy: sortByField || "CreatedDate",
        sortOrder: sorterObj?.order === "ascend" ? "ascend" : "desc",
      });
    } else {
      setSorter({ sortBy: "CreatedDate", sortOrder: "desc" });
    }
  };

  const exportHandler = async () => {
    try {
      const { data: res } = await GetWalletReportApi(
        buildParams({
          page,
          pageSize,
          searchText,
          userType,
          sorter,
          applyPagination: false,
        }),
      );
      if (res?.success && res?.data?.length) {
        return {
          data: res.data.map((item) => {
            const { userId, ...rest } = item || {};
            return {
              userId: userId ?? "",
              ...rest,
            };
          }),
          removableColumns: [],
          fileName: "wallet-report",
        };
      }
      return { data: [], removableColumns: [], fileName: "" };
    } catch (error) {
      return { data: [], removableColumns: [], fileName: "" };
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Segmented
          options={[
            { label: "All Clients", value: "All" },
            { label: "IB Clients", value: "IB" },
            { label: "User Clients", value: "User" },
          ]}
          value={userType}
          setValue={(v) => {
            setUserType(v);
            setPage(1);
          }}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Input
            size="large"
            style={{ width: 220 }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => setSearchText(search)}
            prefix={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                  clipRule="evenodd"
                />
              </svg>
            }
            suffix={
              search && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => {
                    setSearchText("");
                    setSearch("");
                  }}
                  viewBox="0 0 24 24"
                  fill="#bebebe"
                  className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              )
            }
          />
          {canExport && (
            <div>
              <ExportHandler exportHandler={exportHandler} />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-end items-center gap-2 mt-4">
        <div className="flex items-center gap-2 px-4 py-[3px] rounded-lg bg-sky-500/10 border border-sky-500/20 w-fit">
          <span className="text-xs font-medium uppercase tracking-wide text-sky-500">
            Total Wallet Balance:
          </span>
          <span className="text-base font-bold text-sky-600">
            {isLoading ? <Spin size="small" /> : formatAmount(totalWalletBalance)}
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-[3px] rounded-lg bg-emerald-500/10 border border-emerald-500/20 w-fit">
          <span className="text-xs font-medium uppercase tracking-wide text-emerald-500">
            Total IB Wallet Balance:
          </span>
          <span className="text-base font-bold text-emerald-600">
            {isLoading ? (
              <Spin size="small" />
            ) : (
              formatAmount(totalIBWalletBalance)
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 px-4 py-[3px] rounded-lg bg-rose-500/10 border border-rose-500/20 w-fit">
          <span className="text-xs font-medium uppercase tracking-wide text-rose-500">
            Total IB Commission:
          </span>
          <span className="text-base font-bold text-rose-600">
            {isLoading ? <Spin size="small" /> : formatAmount(totalIBCommission)}
          </span>
        </div>
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={tableColumns}
            data={data}
            loading={isLoading}
            page={page}
            pageSize={pageSize}
            sortDirections={["ascend", "descend"]}
            onChange={handleTableChange}
          />
        </div>
        <Pagination
          total={totalRecord}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </div>
    </div>
  );
}
